import { getUser } from "@/lib/auth";
import {
  customElementAttributeMappers,
  goToParentModal,
  setCustomElementMetadata,
} from "@/lib/customElements";
import { onFormInvalid } from "@/lib/form";
import { QuoteCalculator } from "@/models";
import { LineItem, QuoteSection } from "@/models/line-item";
import { MaterialFormula } from "@/models/material-formula";
import { QuoteSchema } from "@/models/quote";
import { fromApiModel, toApiModel } from "@/models/quote-line-item";
import { User } from "@/models/user";
import { Warranty } from "@/models/warranty";
import { WORK_ORDER_TYPES, WorkOrderType } from "@/models/work-order";
import { CompanyService } from "@/services/company";
import { InvoiceService } from "@/services/invoice";
import { JobService } from "@/services/job";
import { MaterialService } from "@/services/material";
import { QuoteService } from "@/services/quote";
import { UsersService } from "@/services/user";
import { WarrantiesService } from "@/services/warranty";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, InfoIcon, PlusIcon, ShoppingCart } from "lucide-react";
import { AnimatePresence } from "motion/react";
import React, { createContext, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Checkbox } from "../base";
import { Button } from "../base/button";
import { Card, CardContent } from "../base/card";
import { CurrencyInput } from "../base/currency-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../base/form";
import { Input } from "../base/input";
import { Popover, PopoverContent, PopoverTrigger } from "../base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { Skeleton } from "../base/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../base/tooltip";
import { LineItemsGroup } from "./line-items-group";
import { PriceSummary } from "./price-summary";
import { QuoteSectionComponent } from "./quote-section";

// LineItemsContext for sharing state between sections
interface LineItemsContextType {
  updatedSectionIds: number[];
  refreshSection: () => void;
}

const LineItemsContext = createContext<LineItemsContextType>({
  updatedSectionIds: [],
  refreshSection: () => {},
});

export const useLineItemsContext = () => useContext(LineItemsContext);

const formSchema = QuoteSchema;

type FormData = z.infer<typeof formSchema>;

interface QuoteFormProps {
  apiUrl: string;
  apiStage: string;
  onSubmit?: (data: FormData) => void;
  initialData?: FormData;
  quoteId?: number;
  jobId?: number;
  invoiceId?: number;
  isInvoice?: boolean;
  isDraft?: boolean;
}

interface ErrorSplashProps {
  message: string;
}

function ErrorSplash({ message }: Readonly<ErrorSplashProps>) {
  return (
    <div className="flex gap-2 items-center p-4 rounded-lg bg-destructive/10 text-destructive">
      <AlertCircle className="w-5 h-5" />
      <p>{message}</p>
    </div>
  );
}

function debounceFormData(
  func: (data: FormData) => Promise<void>,
  wait: number
): (data: FormData) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (data: FormData) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(data);
      timeout = null;
    }, wait);
  };
}

function debounce(func: () => Promise<void>, wait: number): () => void {
  let timeout: NodeJS.Timeout | null = null;

  return () => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func();
      timeout = null;
    }, wait);
  };
}

interface SectionRendererProps {
  section: QuoteSection;
  quoteSections: QuoteSection[];
  jobWorkTypes: WorkOrderType[];
  crews: User[];
  getLineItemsForSection: (sectionId?: number | null) => LineItem[];
  getLineItems: () => LineItem[];
  handleLineItemsChange: (items: LineItem[]) => void;
  handleSectionNameChange: (trackingId: string, name: string) => void;
  handleSectionDelete: (trackingId: string) => void;
  handleSectionMoveUp: (trackingId: string) => void;
  handleSectionMoveDown: (trackingId: string) => void;
  handleSectionCollapse: (trackingId: string) => void;
  handleSectionWorkTypeChange: (trackingId: string, workType: string) => void;
  handleSectionCrewChange: (trackingId: string, crewId: number | null) => void;
  taxRate: number;
  additionalMarkup: number;
  apiUrl: string;
  apiStage: string;
  warranties: Warranty[];
  materialFormulas: MaterialFormula[];
  quoteCalculator: QuoteCalculator | null;
  jobId?: number | null;
  companyName?: string | null;
  refreshSection: () => void;
}

function SectionRenderer({ section, ...props }: SectionRendererProps) {
  const memoizedWorkTypes = React.useMemo(() => {
    return section.wotype ? [section.wotype] : [];
  }, [section.wotype]);

  return (
    <QuoteSectionComponent
      key={section.trackingId}
      section={section}
      lineItems={props.getLineItemsForSection(section.id)}
      workOrderTypes={props.jobWorkTypes}
      crews={props.crews}
      hideMoveUpButton={section.order === 0 || props.quoteSections.length < 2}
      hideMoveDownButton={
        section.order === props.quoteSections.length - 1 ||
        props.quoteSections.length < 2
      }
      hideDeleteButton={props.quoteSections.length < 2}
      onSectionNameChange={props.handleSectionNameChange}
      onSectionDelete={props.handleSectionDelete}
      onSectionMoveUp={props.handleSectionMoveUp}
      onSectionMoveDown={props.handleSectionMoveDown}
      onSectionCollapse={props.handleSectionCollapse}
      onSectionWorkTypeChange={props.handleSectionWorkTypeChange}
      onSectionCrewChange={props.handleSectionCrewChange}
      taxRate={props.taxRate}
      additionalMarkup={props.additionalMarkup}
      materialFormulas={props.materialFormulas}
      quoteCalculator={props.quoteCalculator}
      apiUrl={props.apiUrl}
      apiStage={props.apiStage}
    >
      <LineItemsGroup
        items={props.getLineItemsForSection(section.id)}
        sectionId={section.id}
        sectionTrackingId={section.trackingId}
        workOrderType={section.wotype}
        onChange={(items) => {
          const otherLineItems = props
            .getLineItems()
            .filter((item) => item.sectionId !== section.id);
          const allLineItems = [
            ...otherLineItems,
            ...items.map((item) => ({
              ...item,
              sectionId: section.id,
            })),
          ];
          props.handleLineItemsChange(allLineItems);
        }}
        workTypes={memoizedWorkTypes}
        apiUrl={props.apiUrl}
        apiStage={props.apiStage}
        warranties={props.warranties}
        materialFormulas={props.materialFormulas}
        quoteCalculator={props.quoteCalculator}
        jobId={props.jobId}
        companyName={props.companyName}
        sectionName={section.name}
        refreshSection={props.refreshSection}
      />
    </QuoteSectionComponent>
  );
}

export function QuoteForm({
  apiUrl = "",
  apiStage = "",
  onSubmit,
  initialData,
  quoteId,
  jobId,
  invoiceId,
  isInvoice = false,
  isDraft = false,
}: Readonly<QuoteFormProps>) {
  const quoteService = React.useMemo(
    () => new QuoteService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const invoiceService = React.useMemo(
    () => new InvoiceService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const jobService = React.useMemo(
    () => new JobService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const warrantieService = React.useMemo(
    () => new WarrantiesService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const companyService = React.useMemo(
    () => new CompanyService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const materialService = React.useMemo(
    () => new MaterialService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const usersService = React.useMemo(
    () => new UsersService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = React.useState(!!quoteId);
  const [dataLoaded, setDataLoaded] = React.useState(!quoteId);
  const [error, setError] = React.useState<string | null>(null);
  const [invoiceMarkup, setInvoiceMarkup] = React.useState<number>(0);
  const [invNoQtys, setInvNoQtys] = React.useState<number>(0);
  const [invNoPrices, setInvNoPrices] = React.useState<number>(0);
  const [invNoCharge, setInvNoCharge] = React.useState<number>(0);
  const [invLateFee, setInvLateFee] = React.useState<number>(0);
  const [invTotal, setInvTotal] = React.useState<number>(0);
  const [invoiceDate, setInvoiceDate] = React.useState<string | null>(null);
  const [invoiceDueDate, setInvoiceDueDate] = React.useState<string | null>(
    null
  );
  const [isAutoSaving, setIsAutoSaving] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);
  const [jobWorkTypes, setJobWorkTypes] = React.useState<WorkOrderType[]>([]);
  const [crews, setCrews] = React.useState<User[]>([]);
  const [warranties, setWarranties] = React.useState<Warranty[]>([]);
  const [jobSector, setJobSector] = React.useState<string | null>(null);
  const [expirationDays, setExpirationDays] = React.useState<{
    commercial: number;
    residential: number;
  }>({ commercial: 30, residential: 30 });

  const [quoteSections, setQuoteSections] = React.useState<QuoteSection[]>([]);
  const [isAddingSectionLoading, setIsAddingSectionLoading] =
    React.useState(false);
  const [isGeneratingMaterialOrder, setIsGeneratingMaterialOrder] =
    React.useState(false);
  const [isAddSectionPopoverOpen, setIsAddSectionPopoverOpen] =
    React.useState(false);

  // Add this ref to track the latest quoteSections value
  const latestQuoteSectionsRef = React.useRef<QuoteSection[]>([]);

  // Update the ref whenever quoteSections changes
  React.useEffect(() => {
    latestQuoteSectionsRef.current = quoteSections;
  }, [quoteSections]);

  const user = getUser();
  const companyName = localStorage.getItem("raesCompany");

  const [materialFormulas, setMaterialFormulas] = React.useState<
    MaterialFormula[]
  >([]);
  const [quoteCalculator, setQuoteCalculator] =
    React.useState<QuoteCalculator | null>(null);

  // Add effect to fetch material formulas
  React.useEffect(() => {
    const fetchMaterialFormulas = async () => {
      try {
        const formulas = await materialService.getAllMaterialFormulas();
        setMaterialFormulas(formulas);
      } catch (error) {
        console.error("Failed to fetch material formulas:", error);
      }
    };
    fetchMaterialFormulas();
  }, [materialService]);

  const createQuoteCalculator = React.useCallback(async () => {
    if (!jobId) return;
    try {
      const quoteCalculator = await quoteService.createQuoteCalculator({
        qcjid: jobId,
      });
      setQuoteCalculator(quoteCalculator);
      // Handle the fetched quote calculator data as needed
    } catch (error) {
      console.error("Failed to create Quote Calculator:", error);
    }
  }, [quoteService, jobId]);

  // Add effect to fetch quote calculator
  React.useEffect(() => {
    const fetchQuoteCalculator = async () => {
      if (!jobId) return;

      try {
        const calculator = await quoteService.getQuoteCalculatorByJobId(jobId);

        if (!calculator) {
          // If no calculator exists, create a new one
          await createQuoteCalculator();
          return;
        }

        setQuoteCalculator(calculator);
      } catch (error) {
        console.error("Failed to fetch quote calculator:", error);
      }
    };
    fetchQuoteCalculator();
  }, [jobId, quoteService, createQuoteCalculator]);

  const loadJobWorkTypes = React.useCallback(async () => {
    if (!jobId) return;

    try {
      const job = await jobService.getJobById(jobId);

      if (!job.jservice) return;

      const workTypes = job.jservice.split(":").filter(Boolean);
      const validWorkTypes = workTypes.filter((type) =>
        WORK_ORDER_TYPES.includes(type as WorkOrderType)
      ) as WorkOrderType[];

      setJobWorkTypes(validWorkTypes);

      // Store job sector for expiration date calculation
      if (job.jsector) {
        setJobSector(job.jsector);
      }
    } catch (error) {
      console.error("Failed to load job work types:", error);
    }
  }, [jobId, jobService]);

  const loadCompanySettings = React.useCallback(async () => {
    try {
      const company = await companyService.getCompany(1);
      if (company) {
        setExpirationDays({
          commercial: company.coexpdayscom || 30,
          residential: company.coexpdaysres || 30,
        });
      }
    } catch (error) {
      console.error("Failed to load company settings:", error);
    }
  }, [companyService]);

  const calculateExpirationDate = React.useCallback(
    (quoteDate: string) => {
      if (!quoteDate || !jobSector) return null;

      const days =
        jobSector === "Commercial"
          ? expirationDays.commercial
          : expirationDays.residential;

      const date = new Date(quoteDate);
      date.setDate(date.getDate() + days);
      return date.toISOString().split("T")[0];
    },
    [jobSector, expirationDays]
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData || {
      jrjid: jobId || null,
      jrtype: null,
      jruid: user?.uid || null,
      jrdate: new Date().toISOString().split("T")[0],
      jrexpire: null,
      jrneglect: null,
      jrinsclause: null,
      jrwarr: null,
      jrwarr2: null,
      jrnote: null,
      jrterms: null,
      jrtaxrate: 0,
      jrtaxamt: 0,
      jrtotalcost: 0,
      jrdepreq: 0,
      jronote: null,
      jroedts: null,
      jrqty: null,
      lineItems: [],
    },
    shouldFocusError: false,
  });

  const fetchQuoteSections = React.useCallback(async () => {
    if (!quoteId) return;

    try {
      const sections = await quoteService.getQuoteSections(quoteId);
      setQuoteSections(sections);
      return sections;
    } catch (error) {
      console.error("Failed to fetch quote sections:", error);
    }
  }, [quoteId, quoteService]);

  const fetchInvoice = React.useCallback(async () => {
    if (!invoiceId) return;
    try {
      const invoice = await invoiceService.getInvoiceById(invoiceId);
      setInvoiceMarkup(invoice.invmarkup ?? 0);
      setInvNoQtys(invoice.invnoqtys ?? 0);
      setInvNoPrices(invoice.invnoprices ?? 0);
      setInvNoCharge(invoice.invnocharge ?? 0);
      setInvLateFee(invoice.invlatefee ?? 0);
      setInvoiceDate(invoice.invdate ?? null);
      setInvoiceDueDate(invoice.invdatedue ?? null);
    } catch (error) {
      console.error("Failed to fetch invoice:", error);
    }
  }, [invoiceId, invoiceService]);

  const handleAddSection = React.useCallback(
    async (
      name?: string,
      workType?: string
    ): Promise<QuoteSection[] | undefined> => {
      if (!quoteId) return;

      setIsAddingSectionLoading(true);
      try {
        // Use the ref to get the latest state
        const currentSections = latestQuoteSectionsRef.current;
        const newSection: QuoteSection = {
          trackingId: crypto.randomUUID(),
          name: name ?? "New Section",
          wotype: workType ?? null,
          crewId: null,
          isCollapsed: false,
          order: currentSections.length,
        };

        const updatedSections = await quoteService.updateQuoteSections(
          quoteId,
          [...currentSections, newSection]
        );

        return updatedSections;
      } catch (error) {
        console.error("Failed to add section:", error);
        return undefined;
      } finally {
        setIsAddingSectionLoading(false);
      }
    },
    [quoteId, quoteService]
  );

  const createSectionsFromJobWorkTypes = React.useCallback(
    async (existingSections: QuoteSection[]) => {
      if (!jobId || !quoteId || existingSections.length > 0 || jobWorkTypes.length === 0)
        return;

      try {
        // Create all sections locally first
        const newSections: QuoteSection[] = jobWorkTypes.map((workType, index) => ({
          trackingId: crypto.randomUUID(),
          name: workType,
          wotype: workType,
          crewId: null,
          isCollapsed: false,
          order: index,
        }));

        // Make a single API call to save all sections
        const updatedSections = await quoteService.updateQuoteSections(
          quoteId,
          [...existingSections, ...newSections]
        );

        setQuoteSections(updatedSections);
      } catch (error) {
        console.error("Failed to create sections from job work types:", error);
      }
    },
    [jobId, quoteId, jobWorkTypes, quoteService]
  );

  const fetchQuote = React.useCallback(async () => {
    if (!quoteId) return;

    setIsLoading(true);
    setDataLoaded(false);
    setError(null);
    try {
      const [quoteSections, quote] = await Promise.all([
        fetchQuoteSections(),
        quoteService.getQuoteById(quoteId),
        fetchInvoice(),
      ]);
      form.reset(quote);

      // If expiration is null, calculate it
      const quoteDate = form.getValues("jrdate");
      const currentExpiration = form.getValues("jrexpire");

      if (quoteDate && !currentExpiration) {
        const expirationDate = calculateExpirationDate(quoteDate);
        if (expirationDate) {
          form.setValue("jrexpire", expirationDate);
        }
      }

      if (quoteSections?.length === 0) {
        await createSectionsFromJobWorkTypes(quoteSections);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Failed to get quote:", error);
      setError("Failed to load quote. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [
    quoteId,
    quoteService,
    form,
    fetchQuoteSections,
    fetchInvoice,
    createSectionsFromJobWorkTypes,
    calculateExpirationDate,
  ]);

  React.useEffect(() => {
    loadJobWorkTypes();
  }, [loadJobWorkTypes]);

  React.useEffect(() => {
    loadCompanySettings();
  }, [loadCompanySettings]);

  React.useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const handleSectionNameChange = async (trackingId: string, name: string) => {
    const updatedSections = quoteSections.map((section) =>
      section.trackingId === trackingId ? { ...section, name } : section
    );

    setQuoteSections(updatedSections);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
      } catch (error) {
        console.error("Failed to update section name:", error);
      }
    }
  };

  const handleSectionDelete = async (trackingId: string) => {
    const sectionToDelete = quoteSections.find(
      (section) => section.trackingId === trackingId
    );
    if (!sectionToDelete) return;

    const updatedSections = quoteSections.filter(
      (section) => section.trackingId !== trackingId
    );

    setQuoteSections(updatedSections);

    const sectionId = sectionToDelete.id;

    const updatedLineItems: LineItem[] = getLineItems().filter(
      (item) => item.sectionId !== sectionId
    );

    handleLineItemsChange(updatedLineItems);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
        await saveQuote(form.getValues());
      } catch (error) {
        console.error("Failed to delete section:", error);
      }
    }
  };

  const handleSectionMoveUp = async (trackingId: string) => {
    const sectionIndex = quoteSections.findIndex(
      (s) => s.trackingId === trackingId
    );
    if (sectionIndex <= 0) return;

    const updatedSections = [...quoteSections];
    const temp = updatedSections[sectionIndex];
    updatedSections[sectionIndex] = updatedSections[sectionIndex - 1];
    updatedSections[sectionIndex - 1] = temp;

    updatedSections.forEach((section, idx) => {
      section.order = idx;
    });

    setQuoteSections(updatedSections);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
      } catch (error) {
        console.error("Failed to reorder sections:", error);
      }
    }
  };

  const handleSectionMoveDown = async (trackingId: string) => {
    const sectionIndex = quoteSections.findIndex(
      (s) => s.trackingId === trackingId
    );
    if (sectionIndex < 0 || sectionIndex >= quoteSections.length - 1) return;

    const updatedSections = [...quoteSections];
    const temp = updatedSections[sectionIndex];
    updatedSections[sectionIndex] = updatedSections[sectionIndex + 1];
    updatedSections[sectionIndex + 1] = temp;

    updatedSections.forEach((section, idx) => {
      section.order = idx;
    });

    setQuoteSections(updatedSections);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
      } catch (error) {
        console.error("Failed to reorder sections:", error);
      }
    }
  };

  const handleSectionCollapse = (trackingId: string) => {
    setQuoteSections((sections) =>
      sections.map((section) =>
        section.trackingId === trackingId
          ? { ...section, isCollapsed: !section.isCollapsed }
          : section
      )
    );
  };

  const handleSectionWorkTypeChange = async (
    trackingId: string,
    workType: string
  ) => {
    const updatedSections = quoteSections.map((section) =>
      section.trackingId === trackingId
        ? { ...section, wotype: workType }
        : section
    );

    setQuoteSections(updatedSections);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
      } catch (error) {
        console.error("Failed to update section work type:", error);
      }
    }
  };

  const handleLineItemsChange = React.useCallback(
    (lineItems: LineItem[]) => {
      const quoteLineItems = lineItems.map((item) => {
        const apiModel = toApiModel(item);
        apiModel.jrpcost =
          (apiModel.jrpunits ?? 0) *
          (apiModel.jrpunitcost ?? 0) *
          (1 + (apiModel.jrpunitmarkup ?? 0) / 100);
        return apiModel;
      });
      form.setValue("lineItems", quoteLineItems);
    },
    [form]
  );

  const getLineItems = React.useCallback((): LineItem[] => {
    const formLineItems = form.getValues("lineItems") || [];
    return formLineItems.map((item) => fromApiModel(item));
  }, [form]);

  const getLineItemsForSection = React.useCallback(
    (sectionId?: number | null): LineItem[] => {
      return getLineItems().filter((item) => item.sectionId === sectionId);
    },
    [getLineItems]
  );

  const onFormSubmit = async (data: FormData) => {
    try {
      if (quoteId) {
        await quoteService.updateQuote(quoteId, {
          ...data,
          jrid: quoteId,
        });
      } else {
        await quoteService.createQuote(data);
      }
      onSubmit?.(data);
      goToParentModal("raes-quotes", "viewQuotes1");
    } catch (error) {
      console.error("Failed to save quote:", error);
    }
  };

  const saveQuote = React.useCallback(
    async (data: FormData) => {
      if (!quoteId || !dataLoaded) return;

      setIsAutoSaving(true);
      try {
        await quoteService.updateQuote(quoteId, {
          ...data,
          jrid: quoteId,
        });
        // console.log("data", data); // TODO: IMPORTANT: RE-ENABLE AUTO-SAVE
        setLastSavedAt(new Date());
      } catch (error) {
        console.error("Failed to auto-save quote:", error);
      } finally {
        setIsAutoSaving(false);
      }
    },
    [quoteId, quoteService, dataLoaded]
  );

  const debouncedSave = React.useMemo(
    () => debounceFormData(saveQuote, 500),
    [saveQuote]
  );

  React.useEffect(() => {
    if (!dataLoaded) return;

    const calculatePrices = (data: FormData) => {
      if (quoteId) {
        debouncedSave({ ...data, jrtotalcost: 0 } as FormData);
      }
    };

    const subscription = form.watch(() => {
      calculatePrices(form.getValues() as FormData);
    });

    calculatePrices(form.getValues() as FormData);

    return () => subscription.unsubscribe();
  }, [form, quoteId, debouncedSave, dataLoaded]);

  const saveInvoiceSettings = React.useCallback(
    async (
      markup: number,
      noQtys: number,
      noPrices: number,
      noCharge: number,
      lateFee: number,
      total: number
    ) => {
      if (!invoiceId) return;
      try {
        await invoiceService.updateInvoice(invoiceId, {
          invid: invoiceId,
          invmarkup: markup,
          invnoqtys: noQtys,
          invnoprices: noPrices,
          invnocharge: noCharge,
          invlatefee: lateFee,
          invtotal: total,
        });

        debouncedSave({ ...form.getValues(), jrtotalcost: total } as FormData);
      } catch (error) {
        console.error("Failed to update invoice settings:", error);
      }
    },
    [invoiceId, invoiceService, form, debouncedSave]
  );

  const debouncedSaveInvoiceSettings = React.useMemo(
    () => debounce(async () => {
      await saveInvoiceSettings(
        invoiceMarkup,
        invNoQtys,
        invNoPrices,
        invNoCharge,
        invLateFee,
        invTotal
      );
    }, 500),
    [saveInvoiceSettings, invoiceMarkup, invNoQtys, invNoPrices, invNoCharge, invLateFee, invTotal]
  );

  React.useEffect(() => {
    if (!dataLoaded) return;
    debouncedSaveInvoiceSettings();
  }, [debouncedSaveInvoiceSettings, dataLoaded]);

  // Call saveInvoiceSettings immediately on initial load when dataLoaded becomes true
  React.useEffect(() => {
    if (dataLoaded && invoiceId) {
      saveInvoiceSettings(
        invoiceMarkup,
        invNoQtys,
        invNoPrices,
        invNoCharge,
        invLateFee,
        invTotal
      );
    }
  }, [dataLoaded, invoiceId, saveInvoiceSettings, invoiceMarkup, invNoQtys, invNoPrices, invNoCharge, invLateFee, invTotal]);

  const renderInvoiceFields = () => {
    if (!isInvoice) return null;

    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          <FormItem>
            <FormLabel>Invoice Date</FormLabel>
            <FormControl>
              <Input type="date" value={invoiceDate || ""} disabled readOnly />
            </FormControl>
          </FormItem>

          <FormItem>
            <FormLabel>Invoice Due Date</FormLabel>
            <FormControl>
              <Input
                type="date"
                value={invoiceDueDate || ""}
                disabled
                readOnly
              />
            </FormControl>
          </FormItem>
        </div>

        <FormItem>
          <FormLabel>Late Fee</FormLabel>
          <FormControl>
            <CurrencyInput
              value={invLateFee}
              onChange={(value) => setInvLateFee(value ?? 0)}
            />
          </FormControl>
        </FormItem>
      </>
    );
  };

  React.useEffect(() => {
    const fetchWarranties = async () => {
      try {
        const warranties = await warrantieService.getAllWarranties();
        setWarranties(warranties);
      } catch (error) {
        console.error("Failed to fetch warranties:", error);
      }
    };
    fetchWarranties();
  }, [warrantieService]);

  // Add this new state for tracking which sections need to be refreshed
  const [updatedSectionIds, setUpdatedSectionIds] = useState<number[]>([]);

  // Function to mark a section as needing refresh
  const refreshSection = () => {
    // When a photo moves between sections, refresh all sections to ensure consistency
    const allSectionIds = quoteSections
      .map((section) => section.id)
      .filter((id): id is number => id !== undefined && id !== null);

    setUpdatedSectionIds(allSectionIds);

    // Clear the refresh flag after a short delay
    setTimeout(() => {
      setUpdatedSectionIds([]);
    }, 100);
  };

  // Add effect to fetch crews
  React.useEffect(() => {
    const fetchCrews = async () => {
      try {
        const subcontractors = await usersService.getAllSubcontractors();
        setCrews(subcontractors);
      } catch (error) {
        console.error("Failed to fetch crews:", error);
      }
    };
    fetchCrews();
  }, [usersService]);

  const handleSectionCrewChange = async (
    trackingId: string,
    crewId: number | null
  ) => {
    const updatedSections = quoteSections.map((section) =>
      section.trackingId === trackingId ? { ...section, crewId } : section
    );
    setQuoteSections(updatedSections);

    if (quoteId) {
      try {
        await quoteService.updateQuoteSections(quoteId, updatedSections);
      } catch (error) {
        console.error("Failed to update section crew:", error);
      }
    }
  };

  if (error) {
    return <ErrorSplash message={error} />;
  }

  if (isLoading) {
    return (
      <div className="w-full space-y-6 @container">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="w-24 h-6" />
          </div>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="w-full h-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <LineItemsContext.Provider value={{ updatedSectionIds, refreshSection }}>
      <Form {...form}>
        <form
          noValidate
          onSubmit={form.handleSubmit(onFormSubmit, (errors, event) =>
            onFormInvalid({
              form,
              errors,
              event,
              container: contentRef.current,
            })
          )}
          className="flex overflow-hidden relative flex-col w-full h-full"
        >
          <div className="flex sticky top-0 justify-between items-center py-2 border-b bg-background">
            <div className="flex gap-2 items-center">
              <FormItem className="flex gap-2 items-center">
                <FormControl>
                  <div className="flex gap-1 items-center">
                    <Checkbox
                      checked={invNoQtys === 1}
                      onCheckedChange={(checked: boolean) => {
                        setInvNoQtys(checked ? 1 : 0);
                      }}
                    />
                    <span className="text-sm">Hide Quantities</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="w-4 h-4 text-primary" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Check this box to hide your quantity and unit columns
                          from your customer's view. All cost, tax, and markup
                          fields are always hidden.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </FormControl>
              </FormItem>

              <FormItem className="flex gap-2 items-center">
                <FormControl>
                  <div className="flex gap-1 items-center">
                    <Checkbox
                      checked={invNoPrices === 1}
                      onCheckedChange={(checked: boolean) => {
                        setInvNoPrices(checked ? 1 : 0);
                      }}
                    />
                    <span className="text-sm">Hide Prices</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="w-4 h-4 text-primary" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Check this box to hide your line item price column
                          from your customer's view. All cost, tax, and markup
                          fields are always hidden.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </FormControl>
              </FormItem>
            </div>
            <div className="flex gap-2 items-center">
              <div className="text-sm text-muted-foreground">
                {isAutoSaving ? (
                  <span>Saving...</span>
                ) : lastSavedAt ? (
                  <span>Last saved at {lastSavedAt.toLocaleTimeString()}</span>
                ) : null}
              </div>
              {!isInvoice && isDraft && jobId && quoteId && (
                <Button
                  variant="outline"
                  disabled={isGeneratingMaterialOrder}
                  type="button"
                  size="sm"
                  onClick={async () => {
                    try {
                      setIsGeneratingMaterialOrder(true);
                      materialService
                        .generateMaterialOrder(quoteId)
                        .then(() => {
                          (
                            window as unknown as {
                              goM: (
                                path: string,
                                fn: string,
                                id: string
                              ) => void;
                            }
                          ).goM(
                            "raes-materialOrdersView",
                            "view2",
                            jobId.toString()
                          );
                        })
                        .finally(() => {
                          setIsGeneratingMaterialOrder(false);
                        });
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                >
                  {" "}
                  {isGeneratingMaterialOrder ? (
                    <div className="mr-2 w-4 h-4 rounded-full border-2 border-current animate-spin border-t-transparent" />
                  ) : (
                    <ShoppingCart className="mr-2 w-4 h-4" />
                  )}
                  View Material Order(s)
                </Button>
              )}
            </div>
          </div>
          <div
            ref={contentRef}
            className="flex-1 p-6 space-y-6 overflow-y-auto @container relative"
          >
            <div className="grid grid-cols-1 gap-4 @xl:gap-2">
              {quoteSections.length > 0 ? (
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {quoteSections
                      .sort((a, b) => a.order - b.order)
                      .map((section) => (
                        <SectionRenderer
                          key={section.trackingId}
                          section={section}
                          quoteSections={quoteSections}
                          jobWorkTypes={jobWorkTypes}
                          crews={crews}
                          getLineItemsForSection={getLineItemsForSection}
                          getLineItems={getLineItems}
                          handleLineItemsChange={handleLineItemsChange}
                          handleSectionNameChange={handleSectionNameChange}
                          handleSectionDelete={handleSectionDelete}
                          handleSectionMoveUp={handleSectionMoveUp}
                          handleSectionMoveDown={handleSectionMoveDown}
                          handleSectionCollapse={handleSectionCollapse}
                          handleSectionWorkTypeChange={
                            handleSectionWorkTypeChange
                          }
                          handleSectionCrewChange={handleSectionCrewChange}
                          taxRate={form.watch("jrtaxrate") || 0}
                          additionalMarkup={invoiceMarkup}
                          apiUrl={apiUrl}
                          apiStage={apiStage}
                          warranties={warranties}
                          materialFormulas={materialFormulas}
                          quoteCalculator={quoteCalculator}
                          jobId={jobId}
                          companyName={companyName}
                          refreshSection={refreshSection}
                        />
                      ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="p-4 text-center rounded-md border bg-muted/20">
                  <p>No sections yet. Add a section to get started.</p>
                </div>
              )}
              <Popover
                open={isAddSectionPopoverOpen}
                onOpenChange={setIsAddSectionPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    className="h-12"
                    disabled={
                      isAddingSectionLoading || jobWorkTypes.length === 0
                    }
                  >
                    <div className="flex justify-center items-center w-full h-full">
                      {isAddingSectionLoading ? (
                        <div className="mr-2 w-4 h-4 rounded-full border-2 border-current animate-spin border-t-transparent" />
                      ) : (
                        <PlusIcon className="mr-2 w-4 h-4" />
                      )}
                      Add Section
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="center">
                  <div className="p-2">
                    <p className="mb-2 text-sm font-medium">
                      Select service type:
                    </p>
                    <div className="grid gap-1">
                      {[...jobWorkTypes].sort().map((workType) => (
                        <Button
                          key={workType}
                          variant="ghost"
                          className="justify-start"
                          onClick={async () => {
                            const updatedSections = await handleAddSection(
                              workType,
                              workType
                            );
                            if (updatedSections) {
                              setQuoteSections(updatedSections);
                            }
                            setIsAddSectionPopoverOpen(false);
                          }}
                        >
                          {workType}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-1 @md:gap-6 @md:grid-cols-4">
              <div></div>
              <div></div>
              <div></div>
              <div className="grid grid-cols-1 gap-6">
                {!isInvoice && (
                  <FormItem>
                    <FormLabel>Additional Markup %</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={invoiceMarkup}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setInvoiceMarkup(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
                <FormField
                  control={form.control}
                  name="jrtaxrate"
                  disabled={isInvoice}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax Rate %</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          value={field.value ?? undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <PriceSummary
                  lineItems={getLineItems()}
                  taxRate={form.watch("jrtaxrate") || 0}
                  additionalMarkup={invoiceMarkup}
                  onTotalChange={setInvTotal}
                  invoiceLateFee={invLateFee}
                  isInvoice={isInvoice}
                />

                {isInvoice && (
                  <FormItem className="flex gap-2 items-center">
                    <FormControl>
                      <div className="flex gap-1 items-center">
                        <Checkbox
                          checked={invNoCharge === 1}
                          onCheckedChange={(checked: boolean) => {
                            setInvNoCharge(checked ? 1 : 0);
                          }}
                        />
                        <span className="text-sm">Check if No Charge</span>
                      </div>
                    </FormControl>
                  </FormItem>
                )}

                <FormField
                  control={form.control}
                  name="jrterms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Terms</FormLabel>
                      <Select
                        {...field}
                        onValueChange={(value) => {
                          field.onChange(value === "None" ? "" : value);
                          if (value === "50% Down - Balance Upon Completion") {
                            form.setValue("jrdepreq", invTotal / 2);
                          }
                        }}
                        defaultValue={field.value ?? undefined}
                        value={field.value ?? ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Terms" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="None">None</SelectItem>
                          <SelectItem value="See Description">
                            See Description
                          </SelectItem>
                          <SelectItem value="Upon Receipt">
                            Upon Receipt
                          </SelectItem>
                          <SelectItem value="Upon Completion">
                            Upon Completion
                          </SelectItem>
                          <SelectItem value="NET30">NET30</SelectItem>
                          <SelectItem value="NET60">NET60</SelectItem>
                          <SelectItem value="NET90">NET90</SelectItem>
                          <SelectItem value="NET180">NET180</SelectItem>
                          <SelectItem value="50% Down - Balance Upon Completion">
                            50% Down - Balance Upon Completion
                          </SelectItem>
                          <SelectItem value="As Per Insurance Company Payout">
                            As Per Insurance Company Payout
                          </SelectItem>
                          <SelectItem value="As Per New Construction Pay Request">
                            As Per New Construction Pay Request
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {renderInvoiceFields()}

                <FormField
                  control={form.control}
                  name="jrdepreq"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>Deposit Required</FormLabel>
                        <FormControl>
                          <CurrencyInput
                            name={field.name}
                            ref={field.ref}
                            onChange={field.onChange}
                            value={field.value ?? 0}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </LineItemsContext.Provider>
  );
}

QuoteForm.displayName = "QuoteForm";

setCustomElementMetadata({
  component: QuoteForm,
  tagName: "raes-quote-form",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
    "quote-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "quoteId",
    }),
    "job-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "jobId",
    }),
    "invoice-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "invoiceId",
    }),
    "is-invoice": (value: string) => ({
      ...customElementAttributeMappers.boolean(value),
      prop: "isInvoice",
    }),
    "is-draft": (value: string) => ({
      ...customElementAttributeMappers.boolean(value),
      prop: "isDraft",
    }),
  },
});
